// «Написать» (HomeMail): a window at the bottom right — from which mailbox, to, copy, subject, a letter template of the team,
// the text in the core's block editor (markdown; the server sends it as HTML with a plain-text part) and files. The draft saves itself
// while a person writes; Ctrl+Enter sends; closing keeps the draft in «Черновики».

import { useCallback, useEffect, useRef, useState } from 'react';
import { BlockEditor } from '../../editor/BlockEditor.tsx';
import { LETTER_KINDS } from '../../editor/kinds.ts';
import { recordTitle, templateToken, type RecordObj } from '@confdeck/core-shared';
import { useSync } from '../../sync/react.tsx';
import { Icon } from '../../ui/icons.tsx';
import { IconButton, Button, Menu, MenuContent, MenuItem, MenuLabel, MenuTrigger, Select, useToast } from '../../ui/primitives.tsx';
import { closeWhenSaved, draftInput, draftUnsaved, type Draft } from './drafts.ts';
import { useUnsaved } from '../../shell/unsaved.ts';
import { fileSize, type Attachment } from './model.ts';
import type { Mailbox } from './use-letters.ts';
import s from './mail.module.css';

const AUTOSAVE_MS = 1200;

export function Compose({ initial, boxes, onClose }: { initial: Draft; boxes: Mailbox[]; onClose(): void }) {
  const client = useSync();
  const toast = useToast();
  const [d, setD] = useState<Draft>(initial);
  const [copies, setCopies] = useState(!!(initial.cc || initial.bcc));
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'sending'>(initial.saved ? 'saved' : 'idle');
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(0);
  const saved = useRef(initial.saved ? JSON.stringify(draftInput(initial)) : '');
  const latest = useRef(d);
  latest.current = d;
  const fileRef = useRef<HTMLInputElement | null>(null);
  const toRef = useRef<HTMLInputElement | null>(null);
  // A template put in from the menu replaces the editor's text: the editor takes a new text only while nobody writes in it.
  const [textRev, setTextRev] = useState(0);
  const mb = boxes.find((b) => b.id === d.mailboxId);

  useEffect(() => { if (!d.to) toRef.current?.focus(); }, []);
  const set = (patch: Partial<Draft>) => { setD((p) => ({ ...p, ...patch })); setError(null); };

  /** Saves the draft when it changed since the last save; an empty new letter is not kept. */
  const save = useCallback(async (): Promise<boolean> => {
    const x = latest.current;
    if (!draftUnsaved(x, saved.current)) return true;
    const sig = JSON.stringify(draftInput(x));
    setState('saving');
    const r = await client.run('mail.saveDraft', draftInput(x));
    if (!r.ok) { setState('idle'); setError(`Черновик не сохранён: ${r.error.message}`); return false; }
    saved.current = sig;
    latest.current = { ...x, saved: true };
    setD((p) => ({ ...p, saved: true }));
    setState('saved');
    return true;
  }, [client]);

  useEffect(() => {
    if (state === 'sending') return;
    const t = setTimeout(() => void save(), AUTOSAVE_MS);
    return () => clearTimeout(t);
  }, [d, save, state]);

  const send = async () => {
    if (state === 'sending' || uploading) return;
    setState('sending');
    const r = await client.run('mail.sendLetter', draftInput(latest.current));
    if (!r.ok) { setState('idle'); setError(r.error.message); return; }
    toast({ kind: 'info', text: mb?.kind === 'demo' ? 'Письмо сохранено в «Отправленных» — демо-ящик письма наружу не отправляет' : 'Письмо отправляется' });
    onClose();
  };
  const close = () => closeWhenSaved(save, onClose);
  // Leaving the mail with a letter not yet saved (the autosave waits for a pause) asks first, as every screen does.
  useUnsaved(state !== 'sending' && draftUnsaved(d, saved.current), save, 'письмо');
  const discard = async () => {
    if (latest.current.saved) await client.run('mail.discardDraft', { id: latest.current.id });
    onClose();
  };

  // The files are copied out of the input first: clearing the input empties its list.
  const attach = async (files: File[]) => {
    if (!files.length) return;
    // A file goes into a saved draft: the letter exists, so the file is the letter's own.
    if (!latest.current.saved) {
      const r = await client.run('mail.saveDraft', draftInput(latest.current));
      if (!r.ok) { setError(r.error.message); return; }
      latest.current = { ...latest.current, saved: true };
      setD((p) => ({ ...p, saved: true }));
    }
    for (const f of files) {
      setUploading((n) => n + 1);
      try {
        const res = await fetch(`/api/mail/letters/${latest.current.id}/files`, { method: 'POST', headers: { 'content-type': 'application/octet-stream', 'x-file-name': encodeURIComponent(f.name) }, body: f });
        const j = await res.json() as Attachment & { error?: { message: string } };
        if (!res.ok) throw new Error(j.error?.message ?? 'Файл не загрузился');
        setD((p) => ({ ...p, attachments: [...p.attachments, { name: j.name, size: j.size, type: j.type, url: j.url }] }));
      } catch (e) { setError((e as Error).message); }
      finally { setUploading((n) => n - 1); }
    }
  };

  const templates = useTemplates();
  const applyTemplate = (t: RecordObj) => {
    const vars = varsFor(client, d.to);
    const subject = fill(String(t.data.subject ?? ''), vars);
    const body = fill(String(t.data.body ?? ''), vars);
    set({ subject: d.subject.trim() ? d.subject : subject, text: body + (d.text.trim() ? `\n\n${latest.current.text}` : '') });
    setTextRev((n) => n + 1);
  };

  const title = d.subject.trim() || (d.replyTo ? 'Ответ' : 'Новое письмо');
  return (
    <section className={s.compose} role="dialog" aria-label="Написать письмо" aria-modal="false"
      onKeyDownCapture={(e) => {
        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); void send(); }
        // Esc closes the window from the text too, before the editor takes it to leave the text; only an open «/» list keeps it.
        else if (e.key === 'Escape' && !document.querySelector('[role="listbox"][aria-label="Вид блока"]')) { e.preventDefault(); e.stopPropagation(); void close(); }
      }}
      onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); void close(); } }}>
      <header className={s.composeHead}>
        <span className={s.composeTitle}>{title}</span>
        <span className={s.composeState} aria-live="polite">{state === 'saving' ? 'Сохраняю…' : state === 'saved' ? 'Черновик сохранён' : ''}</span>
        <IconButton size="sm" icon="close" label="Закрыть, черновик останется" hint="Закрыть (Esc) — черновик останется" onClick={() => void close()} />
      </header>
      <div className={s.composeFields}>
        {boxes.length > 1 && (
          <label className={s.composeRow}><span className={s.composeLabel}>От</span>
            <Select value={d.mailboxId} onChange={(v) => set({ mailboxId: v })} options={boxes.map((b) => ({ value: b.id, label: `${b.name} <${b.address}>` }))} label="Ящик отправителя" />
          </label>
        )}
        <label className={s.composeRow}><span className={s.composeLabel}>Кому</span>
          <input ref={toRef} className={s.composeInput} value={d.to} onChange={(e) => set({ to: e.target.value })} placeholder="Имя <адрес>, адрес" />
          {!copies && <button type="button" className={s.linkBtn} onClick={() => setCopies(true)}>Копия</button>}
        </label>
        {copies && <>
          <label className={s.composeRow}><span className={s.composeLabel}>Копия</span><input className={s.composeInput} value={d.cc} onChange={(e) => set({ cc: e.target.value })} /></label>
          <label className={s.composeRow}><span className={s.composeLabel}>Скрытая</span><input className={s.composeInput} value={d.bcc} onChange={(e) => set({ bcc: e.target.value })} /></label>
        </>}
        <label className={s.composeRow}><span className={s.composeLabel}>Тема</span><input className={s.composeInput} value={d.subject} onChange={(e) => set({ subject: e.target.value })} /></label>
      </div>
      <div className={s.composeText}>
        <BlockEditor key={textRev} value={d.text} label="Текст письма" kinds={LETTER_KINDS} autoFocus={!!d.to || textRev > 0} leadLine={textRev === 0 && !initial.saved}
          placeholder="Текст письма или «/» для заголовка, списка, цитаты" onChange={(md) => set({ text: md })} />
      </div>
      {(d.attachments.length > 0 || uploading > 0) && (
        <ul className={s.files} aria-label="Вложения">
          {d.attachments.map((a, i) => (
            <li key={a.url || i} className={s.file}>
              <Icon name="file" size={14} /><span className={s.fileName}>{a.name}</span><span className={s.fileSize}>{fileSize(a.size)}</span>
              <button type="button" className={s.fileRemove} aria-label={`Убрать ${a.name}`} onClick={() => set({ attachments: d.attachments.filter((_, j) => j !== i) })}><Icon name="close" size={12} /></button>
            </li>
          ))}
          {uploading > 0 && <li className={s.file}><span className={s.fileName}>Загружаю…</span></li>}
        </ul>
      )}
      {error && <div className={s.notice} data-tone="danger" role="alert">{error}</div>}
      <footer className={s.composeFoot}>
        <Button variant="primary" icon="send" disabled={state === 'sending' || uploading > 0} onClick={() => void send()} title="Отправить (Ctrl+Enter)">Отправить</Button>
        <IconButton icon="paperclip" label="Вложить файл" onClick={() => fileRef.current?.click()} />
        <input ref={fileRef} type="file" multiple hidden onChange={(e) => { const picked = Array.from(e.target.files ?? []); e.target.value = ''; void attach(picked); }} />
        <Menu>
          <MenuTrigger asChild><Button icon="longtext" title="Вставить шаблон письма">Шаблон</Button></MenuTrigger>
          <MenuContent align="start" side="top">
            <MenuLabel>Шаблоны писем</MenuLabel>
            {templates.length ? templates.map((t) => <MenuItem key={t.id} icon="longtext" onSelect={() => applyTemplate(t)}>{String(t.data.name ?? 'Без названия')}</MenuItem>)
              : <MenuItem disabled>Шаблонов пока нет</MenuItem>}
          </MenuContent>
        </Menu>
        <span className={s.spacer} />
        <IconButton icon="trash" label="Удалить черновик" onClick={() => void discard()} />
      </footer>
    </section>
  );
}

/** Templates the team made for letters by hand (no code — the apps' own templates go with their scenarios). */
function useTemplates(): RecordObj[] {
  const client = useSync();
  const t = client.store.meta.typeByKey('email_template');
  if (!t) return [];
  return [...client.store.recordIds(t.id)].map((id) => client.store.record(id)!).filter((r) => r && !r.archivedAt && !r.data.disabled)
    .sort((a, b) => Number(!!a.data.key) - Number(!!b.data.key) || String(a.data.name).localeCompare(String(b.data.name), 'ru'));
}

/** The variables of a template for the first recipient: the record whose email field holds the address (the contact). */
function varsFor(client: ReturnType<typeof useSync>, to: string): Record<string, unknown> {
  const address = /<([^>]+)>/.exec(to.split(',')[0] ?? '')?.[1] ?? to.split(',')[0]?.trim() ?? '';
  const meta = client.store.meta;
  if (address) for (const t of meta.types()) {
    const emails = meta.fields(t.id).filter((f) => f.fieldType === 'email').map((f) => f.key);
    if (!emails.length || t.app === 'mail') continue;
    for (const id of client.store.recordIds(t.id)) {
      const r = client.store.record(id);
      if (r && emails.some((k) => String(r.data[k] ?? '').toLowerCase() === address.toLowerCase())) return { record: { data: r.data, title: recordTitle(r, meta) }, site: { url: location.origin } };
    }
  }
  return { site: { url: location.origin } };
}

/** `{{record.data.name}}` → the value; a variable without a value stays visible, so the person fills it in by hand. */
const fill = (text: string, vars: Record<string, unknown>) => text.replace(templateToken(), (all, path: string) => {
  let v: unknown = vars;
  for (const k of path.split('.')) v = v && typeof v === 'object' ? (v as Record<string, unknown>)[k] : undefined;
  return v === undefined || v === null || v === '' || typeof v === 'object' ? all : String(v);
});
